// Static Ad Instant Resize's form (live's `StaticAdInstantResizeForm`, spec G14-templates): the
// finished ad and its target formats; one tile a distinct ratio, on live's fixed model.
import { Group, Menu, Stack, Text, Tooltip } from "@mantine/core";
import { Check, ChevronRight, PanelsTopLeft } from "lucide-react";
import { useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import { ResizePlatform, type ResizeFormats } from "../../../api/generated/model";
import { ImageTemplateQuote, ImageTemplateRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { GlassPanel } from "../../../ui/GlassPanel";
import { Pill } from "../../../ui/Pill";
import { RatioGlyph } from "../../../ui/RatioGlyph";
import {
  exampleImage,
  ImageSlot,
  ReuseSwitch,
  slotSource,
  StartArrow,
  type FormExample,
  type SlotImage,
  type TemplateFormProps,
} from "./kit";
import { CostChip } from "../TemplateCost";

/** Live's fixed model (`TWEAK_IMAGE_MODEL_ID`), by our catalog id, and its words when it is gone. */
const MODEL = "gemini-3.1-flash-image";
const MODEL_MISSING = "The image model GEMINI_3.1 is not available.";

/** Live's platform presets' ratios (`PLATFORMS`), as the server's `RESIZE_PLATFORM_RATIOS`
 * (a backend test holds them equal): what a pick makes, for the cost chip's count. */
const PLATFORM_RATIOS: Record<ResizePlatform, readonly string[]> = {
  meta: ["4:5", "1:1", "9:16"],
  tiktok: ["9:16"],
  google: ["1:1", "4:5"],
  youtube: ["16:9"],
  x: ["1:1", "16:9"],
  pinterest: ["2:3", "1:1"],
  snapchat: ["9:16"],
  linkedin: ["1:1"],
};

/** Live's platform presets' labels (`PLATFORMS`), in live's order; the server expands each. */
const PLATFORM_LABELS: Record<ResizePlatform, string> = {
  meta: "Meta",
  tiktok: "Tik-Tok",
  google: "Google",
  youtube: "Youtube",
  x: "X (Twitter)",
  pinterest: "Pinterest",
  snapchat: "Snapchat",
  linkedin: "LinkedIn",
};
const PLATFORMS = Object.values(ResizePlatform);

interface Formats {
  platforms: ResizePlatform[];
  ratios: string[];
}

const toggle = <T,>(values: readonly T[], value: T): T[] =>
  values.includes(value) ? values.filter((each) => each !== value) : [...values, value];

/** Live's `getTargetRatios`: the ticked platforms' ratios in live's order, or the ratios picked,
 * each once (Meta, TikTok and Snapchat make three tiles, not five). */
export function targetRatios({ platforms, ratios }: Formats): string[] {
  const picked = platforms.length > 0 ? PLATFORMS.filter((id) => platforms.includes(id)).flatMap((id) => PLATFORM_RATIOS[id]) : ratios;
  return [...new Set(picked)];
}

/** Live's trigger words: the placeholder, the one pick, or "N formats" counting the ticks. */
export function formatsLabel({ platforms, ratios }: Formats): string {
  const picks = platforms.length > 0 ? PLATFORMS.filter((id) => platforms.includes(id)).map((id) => PLATFORM_LABELS[id]) : ratios;
  if (picks.length === 0) return "Select formats";
  if (picks.length === 1) return picks[0]!;
  return `${picks.length} formats`;
}

/**
 * Live's formats picker (`FormatsSelect`): the platforms, each ticked or not, and "Other", a side
 * list of the model's own ratios. The two are exclusive: a pick in one clears the other.
 */
function FormatsPicker(props: { ratios: readonly string[]; value: Formats; onChange: (value: Formats) => void }) {
  const { ratios, value, onChange } = props;
  return (
    <Menu position="top-start" closeOnItemClick={false} withinPortal={false} width={234}>
      <Tooltip label="Target formats" withArrow>
        <Menu.Target>
          <Pill
            icon={<PanelsTopLeft size={16} />}
            label={formatsLabel(value)}
            aria-label={`Target formats: ${formatsLabel(value)}`}
          />
        </Menu.Target>
      </Tooltip>
      <Menu.Dropdown>
        {PLATFORMS.map((id) => (
          <Menu.Item
            key={id}
            rightSection={value.platforms.includes(id) ? <Check size={16} /> : null}
            onClick={() => onChange({ platforms: toggle(value.platforms, id), ratios: [] })}
          >
            {PLATFORM_LABELS[id]}
          </Menu.Item>
        ))}
        <Menu.Sub position="right-start" offset={24}>
          <Menu.Sub.Target>
            {/* Live marks "Other" while ratios are picked by hand. */}
            <Menu.Sub.Item
              disabled={ratios.length === 0}
              leftSection={value.ratios.length > 0 ? <Check size={16} /> : null}
              rightSection={<ChevronRight size={16} />}
            >
              Other
            </Menu.Sub.Item>
          </Menu.Sub.Target>
          <Menu.Sub.Dropdown mah={320} style={{ overflowY: "auto" }}>
            {ratios.map((ratio) => (
              <Menu.Item
                key={ratio}
                leftSection={<RatioGlyph ratio={ratio} />}
                rightSection={value.ratios.includes(ratio) ? <Check size={16} /> : null}
                onClick={() => onChange({ platforms: [], ratios: toggle(value.ratios, ratio) })}
              >
                {ratio}
              </Menu.Item>
            ))}
          </Menu.Sub.Dropdown>
        </Menu.Sub>
      </Menu.Dropdown>
    </Menu>
  );
}

const NO_FORMATS: Formats = { platforms: [], ratios: [] };

interface Values {
  image: SlotImage | null;
  formats: Formats;
}

/** The form's values: the card's example's (its ad and live's four ratios), or empty. */
function valuesOf(example: FormExample | null): Values | null {
  const inputs = example?.inputs;
  if (example === null || inputs?.template !== "static-ad-instant-resize") return null;
  return {
    image: exampleImage(example, inputs.image),
    formats: { platforms: [...inputs.formats.platforms], ratios: [...inputs.formats.ratios] },
  };
}

/**
 * Live's form: "Reuse example" (on fills the card's example, off clears the form; Replicate opens
 * it on), the "Image" slot ("Pick the ad to resize"), and in the footer the formats picker and the
 * round start, off until both are there and while live's model is not offered.
 */
export function InstantResizeForm(props: TemplateFormProps) {
  const { teamId, brandId, above, example, onStarted } = props;
  const [collapsed, setCollapsed] = useState(false);
  const [start] = useState(() => (props.replicate ? valuesOf(example) : null));
  const [reuse, setReuse] = useState(start !== null);
  const [image, setImage] = useState<SlotImage | null>(start?.image ?? null);
  const [formats, setFormats] = useState<Formats>(start?.formats ?? NO_FORMATS);
  const fill = (on: boolean) => {
    const values = on ? valuesOf(example) : null;
    setReuse(values !== null);
    setImage(values?.image ?? null);
    setFormats(values?.formats ?? NO_FORMATS);
  };
  const catalog = useGetCatalog().data;
  const model = catalog?.image_models.find((each) => each.id === MODEL);
  const missing = catalog !== undefined && model === undefined;
  // Live's form value holds both lists, one of them empty.
  const picked: ResizeFormats | null = formats.platforms.length + formats.ratios.length > 0 ? formats : null;
  const inputs =
    image === null || picked === null
      ? null
      : {
          template: "static-ad-instant-resize" as const,
          image: slotSource(image),
          formats: picked,
        };
  const body = inputs === null ? null : { type: "image.template" as const, inputs };
  const request = ImageTemplateRequest.safeParse(body);
  const quote = ImageTemplateQuote.safeParse(body);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success && !missing ? quote.data : undefined,
    request: request.success && !missing ? request.data : undefined,
    onStarted,
  });
  return (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={example} checked={reuse} onChange={fill} />}
      summary={<Text size="sm">Static Ad Instant Resize</Text>}
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <FormatsPicker ratios={model?.aspect_ratios.values ?? []} value={formats} onChange={setFormats} />
          <Group gap={8} wrap="nowrap">
            <CostChip estimate={props.template.estimate} outputs={targetRatios(formats).length} />
            <StartArrow canStart={job.canStart} starting={job.start.isPending} onStart={job.startJob} />
          </Group>
        </Group>
      }
    >
      <Stack gap={12}>
        <ImageSlot
          teamId={teamId}
          brandId={brandId}
          label="Image"
          title="Pick the ad to resize"
          value={image}
          onChange={setImage}
        />
        {missing ? (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            {MODEL_MISSING}
          </Text>
        ) : null}
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        {/* Live's toast, said in the panel (web/AGENTS.md: no toasts). */}
        {job.start.isSuccess ? (
          <Text component="output" size="sm">
            Generation started
          </Text>
        ) : null}
      </Stack>
    </GlassPanel>
  );
}
